import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { computeTax } from './taxEngine.js';
import { COUNTRIES, COUNTRY_LIST } from './data/countries.js';
import { formatAmount, formatSalaryInput, parseSalaryInput, formatPercent } from './utils/format.js';
import { haptic } from './utils/haptic.js';
import { useExchangeRates } from './hooks/useExchangeRates.js';
import { useWindowSize } from './hooks/useWindowSize.js';
import { useAnimatedNumber } from './hooks/useAnimatedNumber.js';

import Header from './components/Header.jsx';
import Footer from './components/Footer.jsx';
import AdUnit from './components/AdUnit.jsx';
import DonutChart from './components/DonutChart.jsx';
import TaxBreakdown from './components/TaxBreakdown.jsx';
import PayPeriodTable from './components/PayPeriodTable.jsx';
import CurrencySelector from './components/CurrencySelector.jsx';
import SalaryInsights from './components/SalaryInsights.jsx';
import CountryComparison from './components/CountryComparison.jsx';

const PERIODS = ['annual', 'monthly', 'biweekly', 'weekly', 'daily', 'hourly'];
const PERIOD_LABELS = { annual: 'Annual', monthly: 'Monthly', biweekly: 'Bi-Weekly', weekly: 'Weekly', daily: 'Daily', hourly: 'Hourly' };

// Read URL params
function readParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    country: params.get('country') || 'US',
    salary: params.get('salary') || '75000',
    period: params.get('period') || 'annual',
    currency: params.get('currency') || '',
    region: params.get('region') || '',
    status: params.get('status') || '',
  };
}

function AnimatedAmount({ value, currency }) {
  const animated = useAnimatedNumber(value, 700);
  return <>{formatAmount(animated, currency)}</>;
}

AnimatedAmount.propTypes = {
  value:    PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
};

export default function App() {
  const init = readParams();
  const countryData = COUNTRIES[init.country] || COUNTRIES['US'];

  const [country, setCountry] = useState(init.country);
  const [salaryInput, setSalaryInput] = useState(formatSalaryInput(init.salary || countryData.defaultSalary));
  const [period, setPeriod] = useState(PERIODS.includes(init.period) ? init.period : 'annual');
  const [region, setRegion] = useState(init.region || countryData.defaultRegion);
  const [filingStatus, setFilingStatus] = useState(init.status || countryData.defaultStatus);
  const [displayCurrency, setDisplayCurrency] = useState(init.currency || countryData.currency);
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [compareVisible, setCompareVisible] = useState(false);

  const debounceRef = useRef(null);
  const { isLive, convert } = useExchangeRates();
  const { isMobile } = useWindowSize();

  // Compute tax whenever inputs change
  const compute = useCallback(() => {
    const salary = parseSalaryInput(salaryInput);
    if (!salary || salary <= 0) { setResult(null); return; }
    const res = computeTax(country, salary, period, region || null, filingStatus);
    setResult(res);
  }, [country, salaryInput, period, region, filingStatus]);

  useEffect(() => { compute(); }, [compute]);

  // Sync URL params (debounced)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const params = new URLSearchParams();
      params.set('country', country);
      params.set('salary', parseSalaryInput(salaryInput).toString());
      params.set('period', period);
      if (displayCurrency) params.set('currency', displayCurrency);
      if (region) params.set('region', region);
      if (filingStatus) params.set('status', filingStatus);
      window.history.replaceState({}, '', `?${params.toString()}`);
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [country, salaryInput, period, displayCurrency, region, filingStatus]);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleShare = useCallback(async () => {
    haptic('tap');
    const url = window.location.href;
    try {
      if (navigator.share && isMobile) {
        await navigator.share({ title: 'My Salary Breakdown — SalaryLens', url });
      } else {
        await navigator.clipboard.writeText(url);
        showToast('Link copied to clipboard!');
      }
    } catch {
      showToast('Could not share — try copying the URL', 'error');
    }
  }, [isMobile, showToast]);

  const handleCountryChange = useCallback((code) => {
    haptic('tap');
    const cd = COUNTRIES[code];
    if (!cd) return;
    setCountry(code);
    setRegion(cd.defaultRegion);
    setFilingStatus(cd.defaultStatus);
    setDisplayCurrency(cd.currency);
    setSalaryInput(formatSalaryInput(cd.defaultSalary));
  }, []);

  const handleSalaryChange = useCallback((e) => {
    const raw = e.target.value.replace(/[^\d.]/g, '');
    setSalaryInput(formatSalaryInput(raw));
  }, []);

  const currentCountryData = COUNTRIES[country] || COUNTRIES['US'];
  const hasRegions = Object.keys(currentCountryData.regions || {}).length > 1;
  const hasStatuses = Object.keys(currentCountryData.statuses || {}).length > 1;

  const convertedNet = result && displayCurrency && displayCurrency !== currentCountryData.currency && convert
    ? convert(result.net, currentCountryData.currency, displayCurrency)
    : result?.net;

  const convertedGross = result && displayCurrency && displayCurrency !== currentCountryData.currency && convert
    ? convert(result.gross, currentCountryData.currency, displayCurrency)
    : result?.gross;

  const convertedTax = result && displayCurrency && displayCurrency !== currentCountryData.currency && convert
    ? convert(result.totalTax, currentCountryData.currency, displayCurrency)
    : result?.totalTax;

  return (
    <div className="app">
      <Header onShare={handleShare} />

      {/* Toast */}
      {toast && (
        <div className={`toast toast--${toast.type}`} role="alert" aria-live="polite">
          <span className="toast__icon">{toast.type === 'success' ? '✓' : '✕'}</span>
          {toast.msg}
        </div>
      )}

      <main className="main">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero__inner">
            <div className="hero__eyebrow">
              <span className="hero__badge">
                <span className="hero__badge-dot" />
                {isLive ? 'Live exchange rates' : 'Offline rates'}
              </span>
              <span className="hero__year">2024/25 Tax Rates</span>
            </div>
            <h1 className="hero__title">
              Know Your Exact<br />
              <span className="hero__title-accent">Take-Home Pay</span>
            </h1>
            <p className="hero__subtitle">
              Accurate salary &amp; tax calculations for 12 countries. Net pay, tax breakdown, budget insights — instantly.
            </p>
          </div>
        </section>

        {/* Calculator Card */}
        <section className="calculator-section">
          <div className="container">
            <div className="calculator">
              {/* Input Panel */}
              <div className="calculator__inputs">
                <h2 className="calculator__inputs-title">Enter Your Details</h2>

                {/* Country Selector */}
                <div className="form-group">
                  <label className="form-label">Country</label>
                  <div className="country-grid">
                    {COUNTRY_LIST.map(({ code, name, flag }) => (
                      <button
                        key={code}
                        className={`country-btn${country === code ? ' active' : ''}`}
                        onClick={() => handleCountryChange(code)}
                        type="button"
                        aria-pressed={country === code}
                      >
                        <span className="country-btn__flag">{flag}</span>
                        <span className="country-btn__name">{name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Salary Input */}
                <div className="form-group">
                  <label className="form-label">Gross Salary</label>
                  <div className="salary-input-wrap">
                    <span className="salary-input__symbol">{currentCountryData.symbol}</span>
                    <input
                      className="salary-input"
                      type="text"
                      inputMode="numeric"
                      value={salaryInput}
                      onChange={handleSalaryChange}
                      placeholder={formatSalaryInput(currentCountryData.defaultSalary)}
                      aria-label="Enter gross salary"
                    />
                  </div>
                </div>

                {/* Pay Period */}
                <div className="form-group">
                  <label className="form-label">Pay Period</label>
                  <div className="period-tabs">
                    {PERIODS.map(p => (
                      <button
                        key={p}
                        className={`period-tab${period === p ? ' active' : ''}`}
                        onClick={() => { haptic('tap'); setPeriod(p); }}
                        type="button"
                        aria-pressed={period === p}
                      >
                        {PERIOD_LABELS[p]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Region */}
                {hasRegions && (
                  <div className="form-group">
                    <label className="form-label">{country === 'US' ? 'State' : country === 'CA' ? 'Province' : 'Region'}</label>
                    <select
                      className="form-select"
                      value={region}
                      onChange={e => { haptic('tap'); setRegion(e.target.value); }}
                      aria-label="Select region"
                    >
                      {Object.entries(currentCountryData.regions).map(([key, val]) => (
                        <option key={key} value={key}>{val}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Filing Status */}
                {hasStatuses && (
                  <div className="form-group">
                    <label className="form-label">Filing Status</label>
                    <div className="status-tabs">
                      {Object.entries(currentCountryData.statuses).map(([key, label]) => (
                        <button
                          key={key}
                          className={`status-tab${filingStatus === key ? ' active' : ''}`}
                          onClick={() => { haptic('tap'); setFilingStatus(key); }}
                          type="button"
                          aria-pressed={filingStatus === key}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Display Currency */}
                <div className="form-group">
                  <label className="form-label">Display Currency</label>
                  <CurrencySelector
                    value={displayCurrency}
                    onChange={(c) => { haptic('tap'); setDisplayCurrency(c); }}
                  />
                </div>

                <p className="calculator__disclaimer">
                  ⚠ Results are estimates for informational purposes only. Consult a qualified tax professional for advice.
                </p>
              </div>

              {/* Results Panel */}
              <div className="calculator__results">
                {!result ? (
                  <div className="results-empty">
                    <div className="results-empty__icon">◈</div>
                    <p>Enter a salary to see your breakdown</p>
                  </div>
                ) : (
                  <div className="results">
                    {/* Summary Cards */}
                    <div className="results__summary">
                      <div className="results__card results__card--net">
                        <span className="results__card-label">Take-Home Pay</span>
                        <span className="results__card-value">
                          <AnimatedAmount value={convertedNet || 0} currency={displayCurrency} />
                        </span>
                        <span className="results__card-period">/ year</span>
                        <span className="results__card-badge">{formatPercent(result.netPercent)} of gross</span>
                      </div>

                      <div className="results__card results__card--tax">
                        <span className="results__card-label">Total Tax</span>
                        <span className="results__card-value">
                          <AnimatedAmount value={convertedTax || 0} currency={displayCurrency} />
                        </span>
                        <span className="results__card-period">/ year</span>
                        <span className="results__card-badge">{formatPercent(result.effectiveRate)} effective rate</span>
                      </div>

                      <div className="results__card results__card--gross">
                        <span className="results__card-label">Gross Salary</span>
                        <span className="results__card-value">
                          <AnimatedAmount value={convertedGross || 0} currency={displayCurrency} />
                        </span>
                        <span className="results__card-period">/ year</span>
                        <span className="results__card-badge">{currentCountryData.flag} {currentCountryData.name}</span>
                      </div>
                    </div>

                    {/* Donut Chart */}
                    <DonutChart
                      netPercent={result.netPercent}
                      taxPercent={result.effectiveRate}
                    />

                    {/* Tax Breakdown */}
                    <TaxBreakdown
                      breakdown={result.breakdown}
                      gross={result.gross}
                      currency={currentCountryData.currency}
                      displayCurrency={displayCurrency}
                      convert={convert}
                    />

                    {/* Share button */}
                    <button className="share-btn" onClick={handleShare} type="button">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                      </svg>
                      Share My Results
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Ad Unit 1 — After tool */}
        <div className="container">
          <AdUnit type="horizontal" />
        </div>

        {/* Pay Period Table */}
        {result && (
          <section className="section">
            <div className="container">
              <PayPeriodTable
                periods={result.periods}
                currency={currentCountryData.currency}
                displayCurrency={displayCurrency}
                convert={convert}
              />
            </div>
          </section>
        )}

        {/* Salary Insights */}
        {result && (
          <section className="section">
            <div className="container">
              <SalaryInsights
                result={result}
                currency={currentCountryData.currency}
                country={country}
                displayCurrency={displayCurrency}
                convert={convert}
              />
            </div>
          </section>
        )}

        {/* Ad Unit 2 — Mid page */}
        <div className="container">
          <AdUnit type="rectangle" />
        </div>

        {/* Country Comparison Toggle */}
        <section className="section">
          <div className="container">
            {!showComparison ? (
              <div className="comparison-cta">
                <div className="comparison-cta__text">
                  <h3>How does your country&apos;s tax compare globally?</h3>
                  <p>See all 12 countries ranked by take-home pay percentage</p>
                </div>
                <button
                  className="comparison-cta__btn"
                  onClick={() => { haptic('tap'); setShowComparison(true); setTimeout(() => setCompareVisible(true), 50); }}
                  type="button"
                >
                  View Global Comparison
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
              </div>
            ) : (
              <div className={`comparison-wrap${compareVisible ? ' visible' : ''}`}>
                <CountryComparison
                  currentSalary={parseSalaryInput(salaryInput)}
                  currentCountry={country}
                  currentPeriod={period}
                  convert={convert}
                  displayCurrency={displayCurrency}
                />
              </div>
            )}
          </div>
        </section>

        {/* Ad Unit 3 — Bottom */}
        <div className="container">
          <AdUnit type="horizontal" />
        </div>

        {/* FAQ Section */}
        <section className="faq-section" id="faq">
          <div className="container">
            <h2 className="faq-section__title">Frequently Asked Questions</h2>
            <div className="faq-list">
              {FAQ_ITEMS.map((item, i) => (
                <FAQItem key={i} question={item.q} answer={item.a} />
              ))}
            </div>
          </div>
        </section>

        {/* Country SEO Links */}
        <section className="country-links-section">
          <div className="container">
            <h2 className="country-links-section__title">Salary Calculators by Country</h2>
            <div className="country-links-grid">
              {COUNTRY_LIST.map(({ code, name, flag }) => (
                <a
                  key={code}
                  href={`https://salarycalculatorapp.com/?country=${code}`}
                  className="country-link-card"
                  onClick={(e) => { e.preventDefault(); handleCountryChange(code); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                >
                  <span className="country-link-card__flag">{flag}</span>
                  <span className="country-link-card__name">{name}</span>
                  <span className="country-link-card__cta">Calculate →</span>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// FAQ Data
const FAQ_ITEMS = [
  {
    q: 'How accurate is the SalaryLens tax calculator?',
    a: 'SalaryLens uses official 2024/25 tax rates, brackets, and deductions for all 12 supported countries. Calculations are highly accurate for standard employment income. Complex situations (self-employment, investments, multiple incomes) may differ — always verify with a qualified tax advisor.'
  },
  {
    q: 'Which countries are supported?',
    a: 'We support USA (all 50 states + D.C.), United Kingdom (England, Scotland, Wales, N. Ireland), India (New & Old tax regime), Canada (8 provinces), Australia, Germany, France, Singapore, UAE, Japan, Netherlands, and Sweden — with more countries being added regularly.'
  },
  {
    q: 'Does SalaryLens handle state/provincial taxes?',
    a: 'Yes. For the USA, we calculate federal tax plus individual state income tax for all 50 states and Washington D.C. For Canada, we calculate federal tax plus provincial tax for all 8 major provinces.'
  },
  {
    q: 'How does the currency conversion work?',
    a: 'SalaryLens fetches live exchange rates from the open.er-api.com API, refreshed every 30 minutes. If live rates are unavailable, it falls back to reliable cached rates. You can display your results in any of 30 supported currencies.'
  },
  {
    q: 'What is included in the tax calculation?',
    a: 'Calculations include income tax (all brackets), social security/national insurance contributions, pension/CPP/CPF contributions, employment insurance, health levies, and all applicable surcharges. The breakdown shows each component individually.'
  },
  {
    q: 'Can I share my salary calculation results?',
    a: 'Yes! Click the "Share" button to copy a direct URL with all your parameters pre-filled. Share it with colleagues, financial advisors, or save it for reference. Your exact calculation is encoded in the URL.'
  },
  {
    q: 'Is my data private and secure?',
    a: 'Absolutely. SalaryLens processes all calculations directly in your browser — no salary data is ever sent to our servers or stored. The tool works entirely client-side for maximum privacy.'
  },
  {
    q: 'What is the 50/30/20 budget rule?',
    a: 'The 50/30/20 rule is a popular personal finance guideline: allocate 50% of net income to needs (rent, groceries, utilities), 30% to wants (dining, entertainment), and 20% to savings and investments. SalaryLens applies this to your monthly net income automatically.'
  }
];

FAQItem.propTypes = {
  question: PropTypes.string.isRequired,
  answer:   PropTypes.string.isRequired,
};

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item${open ? ' open' : ''}`}>
      <button
        className="faq-item__question"
        onClick={() => { haptic('tap'); setOpen(o => !o); }}
        aria-expanded={open}
        type="button"
      >
        <span>{question}</span>
        <span className="faq-item__icon">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="faq-item__answer" role="region">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
}